import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { XIcon } from 'lucide-react';

export function Sheet(sheetProps: ComponentProps<typeof SheetPrimitive.Root>) {
  const { ...props } = sheetProps;
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}
export function SheetTrigger(sheetTriggerProps: ComponentProps<typeof SheetPrimitive.Trigger>) {
  const { ...props } = sheetTriggerProps;
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

export function SheetClose(sheetCloseProps: ComponentProps<typeof SheetPrimitive.Close>) {
  const { ...props } = sheetCloseProps;
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

export function SheetPortal(sheetPortalProps: ComponentProps<typeof SheetPrimitive.Portal>) {
  const { ...props } = sheetPortalProps;
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

export function SheetOverlay(sheetOverlayProps: ComponentProps<typeof SheetPrimitive.Overlay>) {
  const { className, ...props } = sheetOverlayProps;
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
        className
      )}
      {...props}
    />
  );
}

export const sheetVariants = cva(
  'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
  {
    variants: {
      side: {
        top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
        bottom: 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
        left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
        right: 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetContentProps extends ComponentProps<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  isClosable?: boolean;
}

export function SheetContent(sheetContentProps: SheetContentProps) {
  const { side = 'right', className, isClosable = true, children, ...props } = sheetContentProps;
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content data-slot="sheet-content" className={cn(sheetVariants({ side }), className)} {...props}>
        {isClosable && (
          <SheetClose
            className={
              'text-text-tertiary outline-focus-ring absolute top-1 right-1 z-10 grid h-9 w-9 cursor-pointer place-items-center rounded-sm opacity-75 outline-0 transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60'
            }
          >
            <XIcon className={'pointer-events-none size-4 shrink-0'} />
            <span className="sr-only">Close</span>
          </SheetClose>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

export function SheetHeader(sheetHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = sheetHeaderProps;
  return <div data-slot="sheet-header" className={cn('relative flex flex-row gap-2 p-4', className)} {...props} />;
}

export function SheetBody(sheetBodyProps: ComponentProps<'div'>) {
  const { className, ...props } = sheetBodyProps;
  return <div data-slot="sheet-body" className={cn('relative contents space-y-0 text-left', className)} {...props} />;
}

export function SheetFooter(sheetFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = sheetFooterProps;
  return <div data-slot="sheet-footer" className={cn('flex justify-end space-x-3 p-4', className)} {...props} />;
}

export function SheetTitle(sheetTitleProps: ComponentProps<typeof SheetPrimitive.Title>) {
  const { className, ...props } = sheetTitleProps;
  return <SheetPrimitive.Title data-slot="sheet-title" className={cn('mr-9 w-full truncate text-lg font-semibold tracking-tight whitespace-nowrap', className)} {...props} />;
}

export function SheetDescription(sheetDescriptionProps: ComponentProps<typeof SheetPrimitive.Description>) {
  const { className, ...props } = sheetDescriptionProps;
  return <SheetPrimitive.Description data-slot="sheet-description" className={cn('text-sm', className)} {...props} />;
}
